import { useState } from 'react';
import { Plus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CapabilityEditor } from '../shared';
import { CanvasserType } from '@signalvote/shared';
import type { PresetKey, UserCapabilities } from '@signalvote/shared';
import type { CampaignUserDto } from '@signalvote/shared';
import { useVendors, useCreateVendor } from '@/hooks/queries/useVendors';

interface EditCapabilitiesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  volunteer: CampaignUserDto | null;
  onSave: (data: {
    presetKey: PresetKey | null;
    capabilities: UserCapabilities;
    canvasserType: CanvasserType | undefined;
    vendorId: string | null;
  }) => void;
  isPending?: boolean;
  error?: string | null;
}

/** Copy a volunteer's stored capabilities into an editable record. */
function seedCapabilities(volunteer: CampaignUserDto): UserCapabilities {
  const caps: UserCapabilities = {};
  if (volunteer.capabilities) {
    for (const [key, value] of Object.entries(volunteer.capabilities)) {
      caps[key as keyof UserCapabilities] = value;
    }
  }
  return caps;
}

/**
 * Shell: owns nothing but the dialog frame. The form is remounted per person,
 * and Radix unmounts it on close, so it is seeded exactly once per "the user
 * opened this person" — a refetch of the people list can no longer clobber an
 * unsaved permission change.
 */
export function EditCapabilitiesDialog({
  open,
  onOpenChange,
  volunteer,
  onSave,
  isPending = false,
  error = null,
}: EditCapabilitiesDialogProps) {
  if (!volunteer) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <EditCapabilitiesForm
          key={volunteer.userId}
          volunteer={volunteer}
          onOpenChange={onOpenChange}
          onSave={onSave}
          isPending={isPending}
          error={error}
        />
      </DialogContent>
    </Dialog>
  );
}

function EditCapabilitiesForm({
  volunteer,
  onOpenChange,
  onSave,
  isPending,
  error,
}: {
  volunteer: CampaignUserDto;
  onOpenChange: (open: boolean) => void;
  onSave: EditCapabilitiesDialogProps['onSave'];
  isPending: boolean;
  error: string | null;
}) {
  const [presetKey, setPresetKey] = useState<PresetKey | null>(
    (volunteer.presetKey as PresetKey) ?? null,
  );
  const [capabilities, setCapabilities] = useState<UserCapabilities>(() =>
    seedCapabilities(volunteer),
  );
  const [canvasserType, setCanvasserType] = useState<CanvasserType>(
    volunteer.canvasserType ?? CanvasserType.Volunteer,
  );
  const [vendorId, setVendorId] = useState<string | null>(volunteer.vendorId ?? null);
  const [showTypeConfirm, setShowTypeConfirm] = useState(false);
  const [pendingType, setPendingType] = useState<CanvasserType>(CanvasserType.Volunteer);
  const [creatingVendor, setCreatingVendor] = useState(false);
  const [newVendorName, setNewVendorName] = useState('');

  const { data: vendors = [] } = useVendors();
  const { mutate: createVendor, isPending: createVendorPending } = useCreateVendor();

  const handleChange = (newPreset: PresetKey | null, newCaps: UserCapabilities) => {
    setPresetKey(newPreset);
    setCapabilities(newCaps);
  };

  const handleTypeChange = (value: CanvasserType) => {
    // If switching from Paid to non-Paid and has a vendor, show confirmation
    if (
      canvasserType === CanvasserType.Paid &&
      value !== CanvasserType.Paid &&
      vendorId
    ) {
      setPendingType(value);
      setShowTypeConfirm(true);
      return;
    }
    setCanvasserType(value);
    if (value !== CanvasserType.Paid) {
      setVendorId(null);
    }
  };

  const handleConfirmTypeChange = () => {
    setCanvasserType(pendingType);
    setVendorId(null);
    setShowTypeConfirm(false);
  };

  const handleCancelTypeChange = () => {
    setShowTypeConfirm(false);
  };

  const handleCreateVendor = () => {
    const trimmed = newVendorName.trim();
    if (!trimmed) return;
    createVendor(
      { name: trimmed },
      {
        onSuccess: (created) => {
          setVendorId(created.id);
          setCreatingVendor(false);
          setNewVendorName('');
        },
      }
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      presetKey,
      capabilities,
      canvasserType,
      vendorId: canvasserType === CanvasserType.Paid ? vendorId : null,
    });
  };

  const vendorName = volunteer.vendorName || vendors.find((v) => v.id === vendorId)?.name;

  return (
    <form onSubmit={handleSubmit}>
      <DialogHeader>
        <DialogTitle>{volunteer.isOwner ? 'Owner Permissions' : 'Edit Permissions'}</DialogTitle>
        <DialogDescription>
          {volunteer.isOwner
            ? `About ${volunteer.firstName} ${volunteer.lastName}'s permissions`
            : `Change permissions for ${volunteer.firstName} ${volunteer.lastName}`}
        </DialogDescription>
      </DialogHeader>

      <div className="py-4 space-y-4">
        <CapabilityEditor
          presetKey={presetKey}
          capabilities={capabilities}
          onChange={handleChange}
          isOwner={volunteer.isOwner}
        />

        {!volunteer.isOwner && (
          <>
            <div className="space-y-2">
              <label className="text-sm font-medium">Canvasser Type</label>
              <Select
                value={canvasserType}
                onValueChange={(v) => handleTypeChange(v as CanvasserType)}
                data-testid="canvasser-type-select"
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Volunteer">Volunteer</SelectItem>
                  <SelectItem value="Paid">Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {canvasserType === CanvasserType.Paid && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Vendor</label>
                {!creatingVendor ? (
                  <Select
                    value={vendorId ?? 'none'}
                    onValueChange={(value) => {
                      if (value === '__create__') {
                        setCreatingVendor(true);
                      } else {
                        setVendorId(value === 'none' ? null : value);
                      }
                    }}
                    data-testid="vendor-select"
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select vendor (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No vendor</SelectItem>
                      {vendors.map((v) => (
                        <SelectItem key={v.id} value={v.id}>
                          {v.name}
                        </SelectItem>
                      ))}
                      <SelectItem value="__create__">
                        <span className="flex items-center gap-1">
                          <Plus className="h-3 w-3" />
                          Create new vendor
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      placeholder="Vendor name"
                      value={newVendorName}
                      onChange={(e) => setNewVendorName(e.target.value)}
                      autoFocus
                    />
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleCreateVendor}
                      disabled={!newVendorName.trim() || createVendorPending}
                    >
                      Add
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setCreatingVendor(false);
                        setNewVendorName('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            )}

            {showTypeConfirm && (
              <div className="rounded-md border border-warning bg-warning/10 p-3 space-y-2">
                <p className="text-sm">
                  Changing {volunteer.firstName} {volunteer.lastName} from Paid to Volunteer will remove their
                  vendor assignment{vendorName ? ` (${vendorName})` : ''}.
                  Historical shift data is not affected.
                </p>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={handleCancelTypeChange}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleConfirmTypeChange}
                  >
                    Continue
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {error && (
        <div
          role="alert"
          className="mb-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {error}
        </div>
      )}

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={() => onOpenChange(false)}
          disabled={isPending}
        >
          {volunteer.isOwner ? 'Close' : 'Cancel'}
        </Button>
        {!volunteer.isOwner && (
          <Button type="submit" disabled={isPending}>
            {isPending && (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            )}
            Save Changes
          </Button>
        )}
      </DialogFooter>
    </form>
  );
}
